<template>
  <div class="employees-organization-table">
    <UniversalTable
      :config="tableConfigWithFilters"
      :items="paginatedEmployees"
      :loading="isTableLoading"
      :emptyObjectData="{
        singular: 'Employee',
        plural: 'Employees',
        emptyTitle: 'No employees created',
        emptyMessage: 'In an Organization you need Employees to assign to Positions',
        emptyButtonLabel: 'Add your first employee',
        emptyContentMarginTop: '100px',
      }"
      @onClick="viewState.state.filters.showFilterDialog = true"
      @onSearch="handleSearch"
      @onMultipleFieldSearch="handleMultipleFieldSearch"
      @onSort="handleSort"
      @removeFilter="removeFilter"
      @removeAllFilters="removeAllFilters"
      @onActionClick="handleAddEmployee"
      @drilldown="handleEmployeeDrilldown"
      backend-pagination
      @onRequest="handleRequest"
      :externalCurrentPage="currentPage"
      :items-count="filteredCount"
      :cellContainerMaxWidth="tableConfig.tableParams.value.cellContainerMaxWidth"
      :department-filter="ideaMdp440Enabled ? viewState.state.filters.department : null"
      :department-scope="viewState.state.filters.departmentScope"
      @toggleDepartmentScope="handleGridToggleDepartmentScope"
      @clearDepartmentFilter="handleGridClearDepartmentFilter"
    >
      <template #EmployeeLineMenu-activator>
        <div class="icon-container">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            class="icon-svg"
          >
            <path
              d="M12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13Z"
              stroke="#344054"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M19 13C19.5523 13 20 12.5523 20 12C20 11.4477 19.5523 11 19 11C18.4477 11 18 11.4477 18 12C18 12.5523 18.4477 13 19 13Z"
              stroke="#344054"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M5 13C5.55228 13 6 12.5523 6 12C6 11.4477 5.55228 11 5 11C4.44772 11 4 11.4477 4 12C4 12.5523 4.44772 13 5 13Z"
              stroke="#344054"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>
      </template>
    </UniversalTable>
    <FilterEmployeeModal
      v-if="ideaMdp440Enabled"
      :value="viewState.state.filters.showFilterDialog"
      :initial-statuses="filterModalInitialStatuses"
      :initial-include-archived="filterModalInitialIncludeArchived"
      :initial-department="viewState.state.filters.department"
      :initial-scope="viewState.state.filters.departmentScope"
      :departments="gridFilterModalDepartments"
      :loading="gridFilterModalDepartmentsLoading"
      :show-radio-groups="ideaMdp551Enabled"
      :initial-occupies="filterModalRadioPayload.occupies"
      :initial-profile-picture="filterModalRadioPayload.profilePicture"
      :initial-access="filterModalRadioPayload.access"
      :initial-prepares="filterModalRadioPayload.prepares"
      @input="viewState.state.filters.showFilterDialog = $event"
      @close="viewState.state.filters.showFilterDialog = false"
      @search="handleGridDepartmentSearch"
      @apply="handleGridFilterModalApply"
    />
    <SearchModal
      v-else
      :dialog="viewState.state.filters.showFilterDialog"
      @back="viewState.state.filters.showFilterDialog = false"
      @checkedItemsChanged="applyFilters"
      :clearSelection="viewState.state.filters.clearSelection"
      filterType="Employee"
      :filterGroups="viewState.state.filters.filterGroups"
      :statusOptions="viewState.state.filters.statusOptions"
      :modalWidth="800"
      :defaultSelected="viewState.state.filters.values"
    />
    <AddEmployeeDialog
      v-if="!employees.getCurrentEmployee()"
      :show="viewState.state.addEmployeeModal.show"
      @close="viewState.state.addEmployeeModal.show = false"
      @onSave="handleSaveEmployee"
      :propEmployee="null"
    />
    <RenameEmployeeDialog
      v-else
      :show="viewState.state.addEmployeeModal.show"
      @close="viewState.state.addEmployeeModal.show = false"
      @onSave="handleSaveEmployee"
      :propEmployee="
        employees.getCurrentEmployee()
          ? {
              id: employees.getCurrentEmployee()?.id || 0,
              first_name: employees.getCurrentEmployee()?.first_name || '',
              last_name: employees.getCurrentEmployee()?.last_name || '',
              title: employees.getCurrentEmployee()?.title || '',
              email: employees.getCurrentEmployee()?.email || '',
              availability: employees.getCurrentEmployee()?.availability || 0,
              avatar: employees.getCurrentEmployee()?.avatar || '',
              user: employees.getCurrentEmployee()?.user || '',
            }
          : null
      "
    />
    <ActivateEmployeeDialog
      :show="viewState.state.activeEmployeeModal.show"
      :avatar="{
        picture: employees.getCurrentEmployee()?.avatar || '',
        firstName: employees.getCurrentEmployee()?.first_name || 'U',
        lastName: employees.getCurrentEmployee()?.last_name || 'S',
      }"
      :full_name="`${employees.getCurrentEmployee()?.first_name || ''} ${employees.getCurrentEmployee()?.last_name || ''}`"
      @onClose="viewState.state.activeEmployeeModal.show = false"
      @onActiveEmployee="handleActivateEmployee"
    />
    <DeleteEmployeeDialog
      @onAction="handleDeleteEmployee"
      @onClose="viewState.state.deleteEmployeeModal.show = false"
      :show="viewState.state.deleteEmployeeModal.show"
      :avatar="{
        picture: employees.getCurrentEmployee()?.avatar || '',
        firstName: employees.getCurrentEmployee()?.first_name || 'U',
        lastName: employees.getCurrentEmployee()?.last_name || 'S',
      }"
    />
    <TerminateEmployeeDialog
      @onAction="handleTerminateEmployee"
      @onClose="viewState.state.terminateEmployeeModal.show = false"
      :show="viewState.state.terminateEmployeeModal.show"
      :full_name="`${employees.getCurrentEmployee()?.first_name || ''} ${employees.getCurrentEmployee()?.last_name || ''}`"
      :avatar="{
        picture: employees.getCurrentEmployee()?.avatar || '',
        firstName: employees.getCurrentEmployee()?.first_name || 'U',
        lastName: employees.getCurrentEmployee()?.last_name || 'S',
        allData: employees.getCurrentEmployee() || {},
      }"
    />
    <ReactivateEmployeeDialog
      :show="viewState.state.reactivateEmployeeModal.show"
      :avatar="{
        picture: employees.getCurrentEmployee()?.avatar || '',
        firstName: employees.getCurrentEmployee()?.first_name || '',
        lastName: employees.getCurrentEmployee()?.last_name || '',
      }"
      @onClose="viewState.state.reactivateEmployeeModal.show = false"
      @onAction="handleReactivateEmployee"
    />
    <EditModalPicture
      :dialog="viewState.state.changePictureModal.show"
      :avatar="employees.getCurrentEmployee()?.avatar || ''"
      @croppedImage="handleChangePicture"
      @closeModal="viewState.state.changePictureModal.show = false"
      @removeModal="handleRemovePicture"
    />
    <GiveAccessToEmployeeWizard
      v-if="ideaMdp453GiveAccessWizard"
      v-model="viewState.state.giveAccessModal.show"
      :email-check-handler="giveAccessEmailCheck"
      :current-employee="{
        id: employees.getCurrentEmployee()?.id || 0,
        firstName: employees.getCurrentEmployee()?.first_name || '',
        lastName: employees.getCurrentEmployee()?.last_name || '',
        availabilityVal: employees.getCurrentEmployee()?.availability,
        avatar: employees.getCurrentEmployee()?.avatar || '',
        email: employees.getCurrentEmployee()?.email || '',
        primary_position: employees.getCurrentEmployee()?.primary_position,
        preparing_for_positions: employees.getCurrentEmployee()?.preparing_for_positions,
        isAllocated:
          employees.getCurrentEmployee()?.is_allocated ??
          employees.getCurrentEmployee()?.isAllocated ??
          false,
      }"
      @onSave="handleGiveAccess"
    />
    <GiveAccessToEmployeeModal
      v-else
      @checkEmail="handleCheckEmail"
      @onSave="handleGiveAccess"
      @onChangeContentType="viewState.state.giveAccessModal.dataType = $event"
      v-model="viewState.state.giveAccessModal.show"
      :contentTypeProp="viewState.state.giveAccessModal.dataType"
      :existingUser="viewState.state.giveAccessModal.existingUser"
      :access_level="viewState.state.giveAccessModal.access_level"
      :showEmailLoadingIcon="viewState.state.giveAccessModal.showEmailLoadingIcon"
      :current-employee="{
        id: employees.getCurrentEmployee()?.id || 0,
        firstName: employees.getCurrentEmployee()?.first_name || '',
        lastName: employees.getCurrentEmployee()?.last_name || '',
        availabilityVal: employees.getCurrentEmployee()?.availability,
        avatar: employees.getCurrentEmployee()?.avatar || '',
        email: employees.getCurrentEmployee()?.email || '',
        primary_position: employees.getCurrentEmployee()?.primary_position,
        preparing_for_positions: employees.getCurrentEmployee()?.preparing_for_positions,
      }"
    />
    <GiveBackAccessToEmployeeModal
      @onSave="handleGiveBackAccess"
      @onChangeContentType="viewState.state.giveAccessModal.dataType = $event"
      v-model="viewState.state.giveBackAccessModal.show"
      :contentTypeProp="viewState.state.giveAccessModal.dataType"
      :existingUser="viewState.state.giveAccessModal.existingUser"
      :showEmailLoadingIcon="viewState.state.giveAccessModal.showEmailLoadingIcon"
      :current-employee="{
        id: employees.getCurrentEmployee()?.id || 0,
        firstName: employees.getCurrentEmployee()?.first_name || '',
        lastName: employees.getCurrentEmployee()?.last_name || '',
        availabilityVal: employees.getCurrentEmployee()?.availability,
        avatar: employees.getCurrentEmployee()?.avatar || '',
        email: employees.getCurrentEmployee()?.email || '',
      }"
    />
    <ArchiveEmployeeDialog
      v-if="viewState.state.archiveAction === 'archive'"
      :show="viewState.state.archiveEmployeeModal.show"
      :avatar="{
        picture: employees.getCurrentEmployee()?.avatar || '',
        firstName: employees.getCurrentEmployee()?.first_name || '',
        lastName: employees.getCurrentEmployee()?.last_name || '',
      }"
      :full_name="`${employees.getCurrentEmployee()?.first_name || ''} ${employees.getCurrentEmployee()?.last_name || ''}`"
      @onClose="viewState.state.archiveEmployeeModal.show = false"
      @onAction="handleArchiveEmployee"
    />
    <UnarchiveEmployeeDialog
      v-else
      :show="viewState.state.archiveEmployeeModal.show"
      :avatar="{
        picture: employees.getCurrentEmployee()?.avatar || '',
        firstName: employees.getCurrentEmployee()?.first_name || '',
        lastName: employees.getCurrentEmployee()?.last_name || '',
      }"
      @onClose="viewState.state.archiveEmployeeModal.show = false"
      @onAction="handleArchiveEmployee"
    />
    <SuspendEmployeeAccess
      v-if="viewState.state.suspendEmployeeAccessModal?.show"
      :value="viewState.state.suspendEmployeeAccessModal?.show"
      headline="Suspend Access"
      info="You are about to suspend access for"
      actionLabel="Suspend"
      :destructive="true"
      :user="{
        user_avatar: employees.getCurrentEmployee()?.avatar || '',
        firstName: employees.getCurrentEmployee()?.first_name || '',
        lastName: employees.getCurrentEmployee()?.last_name || '',
        user_email: employees.getCurrentEmployee()?.email || '',
      }"
      :can_notify="true"
      :notify="false"
      @onAction="handleSuspendAccess"
      @input="viewState.state.suspendEmployeeAccessModal.show = false"
    />
    <ExActionsPopUp
      :value="viewState.state.viewAsConfirmModal.show"
      headline="Confirm Impersonate"
      :info="viewState.state.viewAsConfirmModal.message || ''"
      actionLabel="Continue"
      :centerHeadline="true"
      actionsTopSpacing="32px"
      @input="viewState.state.viewAsConfirmModal.show = false"
      @onAction="viewState.confirmViewAsAfterStatusWarning()"
    />
    <RestoreEmployeeAccess
      v-if="viewState.state.giveBackEmployeeAccessModal?.show"
      :value="viewState.state.giveBackEmployeeAccessModal?.show"
      headline="Restore Access"
      info="You are about to restore access for"
      actionLabel="Restore"
      :destructive="false"
      :user="{
        user_avatar: employees.getCurrentEmployee()?.avatar || '',
        firstName: employees.getCurrentEmployee()?.first_name || '',
        lastName: employees.getCurrentEmployee()?.last_name || '',
        user_email: employees.getCurrentEmployee()?.email || '',
      }"
      :can_notify="true"
      :notify="false"
      @onAction="handleGiveBackAccessUser"
      @input="viewState.state.giveBackEmployeeAccessModal.show = false"
    />
    <ResendInviteAdminUserPopup
      :show="viewState.state.resendInviteModal.show"
      :user="{
        firstName: employees.getCurrentEmployee()?.first_name || '',
        lastName: employees.getCurrentEmployee()?.last_name || '',
      }"
      @onClose="viewState.state.resendInviteModal.show = false"
      @onResendInviteAdminUser="viewState.handleResendInvite"
    />
    <CancelInviteAdminUserPopup
      :show="viewState.state.cancelInviteModal.show"
      :user="{
        firstName: employees.getCurrentEmployee()?.first_name || '',
        lastName: employees.getCurrentEmployee()?.last_name || '',
      }"
      @onClose="viewState.state.cancelInviteModal.show = false"
      @onCancelInviteAdminUser="viewState.handleCancelInvite"
    />
    <ChecklistDrawer
      :isEmployeeChatter="true"
      ref="drawerContainer"
      :drawer="viewState.state?.openChatterModal?.show"
      :avatar="{
        picture: employees.getCurrentEmployee()?.avatar,
        firstName: employees.getCurrentEmployee()?.first_name || '',
        lastName: employees.getCurrentEmployee()?.last_name || '',
      }"
      :typeValue="
        (employees.getCurrentEmployee()?.first_name || '') +
        ' ' +
        (employees.getCurrentEmployee()?.last_name || '')
      "
      @closeDrawer="viewState.closeEmployeeChatterModal()"
      :type="'Employee'"
      object-type="employee"
      :object-id="employees.getCurrentEmployee()?.id ?? null"
      :chatterList="viewState.state?.openChatterModal?.chatterData"
      :totalCheckpointChatter="viewState.state?.openChatterModal?.totalEmployeeChatter"
      @requestMoreCheckpointsChatter="viewState.requestMoreChatter"
    />
    <AssignEmployeeToPrimaryPositionDialog
      :value="viewState.state.assignmentWizard.show"
      :employeeName="`${employees.getCurrentEmployee()?.first_name || ''} ${employees.getCurrentEmployee()?.last_name || ''}`"
      :employeeFirstName="employees.getCurrentEmployee()?.first_name || ''"
      :employeeLastName="employees.getCurrentEmployee()?.last_name || ''"
      :employeeAvatar="employees.getCurrentEmployee()?.avatar"
      :items="viewState.state.primaryPositions.availablePositions"
      :hasMoreData="viewState.state.primaryPositions.hasMoreData"
      :isLoadingMore="viewState.state.primaryPositions.isLoadingMore"
      :noResults="viewState.state.primaryPositions.searchNoResults"
      :loading="viewState.state.assignmentWizard.isAssigning"
      :canNotifyEmployee="!!employees.getCurrentEmployee()?.email"
      :equalVacancy="viewState.state.primaryPositions.equalVacancy"
      :employeeAvailability="viewState.state.primaryPositions.employeeVacancy"
      :rejectionMessage="viewState.state.assignmentWizard.rejectionMessage"
      @input="!$event ? viewState.closeAssignmentWizard() : ''"
      @date-change="viewState.onAssignmentDateChange"
      @assign="
        viewState.onAssignPositionToEmployee({
          ...$event,
          accountId: accountID,
        })
      "
      @search="viewState.searchPrimaryPositions"
      @load-more="viewState.loadMorePrimaryPositions"
    />
    <ExEmployeeToPreparePosition
      v-if="readinessProductEnabled"
      :label="'Position'"
      :items="viewState.state.preparePositions.availablePositions"
      :currentEmployee="employees.getCurrentEmployee()"
      :value="viewState.state.preparePosition.show"
      :isAssigning="viewState.state.preparePosition.isAssigning"
      @input="!$event ? viewState.closePreparePosition() : ''"
      @onAssignPreparePosition="
        viewState.onAssignPreparePositionToEmployee({
          obj: $event,
          accountId: accountID,
        })
      "
      @load-more="viewState.loadMorePreparePositions"
      @search="viewState.searchPreparePositions"
      :isLoadingMore="viewState.state.preparePositions.isLoadingMore"
      :hasMoreData="viewState.state.preparePositions.hasMoreData"
      :noResults="viewState.state.preparePositions.searchNoResults"
    />
    <RelievePrimaryAssignment
      v-if="viewState.state.relieveAssignment.show"
      :show="viewState.state.relieveAssignment.show"
      :type="viewState.state.relieveAssignment.type"
      :avatar="{
        picture: employees.getCurrentEmployee()?.avatar || '',
        firstName: employees.getCurrentEmployee()?.first_name || '',
        lastName: employees.getCurrentEmployee()?.last_name || '',
        user: employees.getCurrentEmployee()?.user || '',
        allData: employees.getCurrentEmployee() || '',
      }"
      :current-position="viewState.state.relieveAssignment.currentPosition?.name || ''"
      @onAction="viewState.relievePrimaryPositionFromEmployee(accountID, $event)"
      @onClose="viewState.closeRelieveAssignment()"
    />
  </div>
</template>

<script setup>
const emit = defineEmits(["drilldown"]);

const handleEmployeeDrilldown = (data) => {
  emit("drilldown", data);
};
import {
  ref,
  computed,
  watch,
  onMounted,
  onBeforeUnmount,
  onUnmounted,
  getCurrentInstance,
} from "vue";
import UniversalTable from "@components/UniversalTable/UniversalTable.vue";
import { useTableConfig } from "./composables/useTableConfig";

import SearchModal from "@components/SearchBar/SearchModal.vue";
import FilterEmployeeModal from "@components/FilterEmployeeModal/FilterEmployeeModal.vue";
import {
  LD_FLAG_IDEA_MDP_440,
  LD_FLAG_IDEA_MDP_450,
  LD_FLAG_IDEA_MDP_551,
} from "@components/constants/launchDarklyFlagKeys";
import { areDefaultFiltersApplied } from "@components/constants/filterDefaultsRegistry";
import { mapApiDepartmentsToPickerList } from "@components/DepartmentPickerField/departmentPickerFieldUtils.js";
import {
  buildEmployeeRadioFilterPayload,
  payloadToEmployeeRadioFilterValues,
} from "@components/FilterEmployeeModal/employeeFilterPayload";
import AddEmployeeDialog from "@components/organizations/employees/AddEmployeeDialog/AddEmployeeDialog.vue";
import RenameEmployeeDialog from "@components/organizations/employees/RenameEmployeeDialog/RenameEmployeeDialog.vue";
import ActivateEmployeeDialog from "@components/ActivateEmployeeDialog/ActivateEmployeeDialog.vue";
import DeleteEmployeeDialog from "@components/DeleteEmployeeDialog/DeleteEmployeeDialog.vue";
import TerminateEmployeeDialog from "@components/TerminateEmployeeDialog/TerminateEmployeeDialog.vue";
import ReactivateEmployeeDialog from "@components/ReactivateEmployeeDialog/ReactivateEmployeeDialog.vue";
import EditModalPicture from "@components/EditPicture/EditModal.vue";
import GiveAccessToEmployeeWizard from "@components/GiveAccessToEmployeeWizard/GiveAccessToEmployeeWizard.vue";
import { mapCheckEmailApiToGiveAccessHandlerResult } from "@components/GiveAccessToEmployeeWizard/giveAccessToEmployeeEmailValidation.js";
import GiveAccessToEmployeeModal from "@components/GiveAccessEmployee/GiveAccessToEmployeeModal.vue";
import GiveBackAccessToEmployeeModal from "@components/GiveBackAccessEmployee/GiveBackAccessToEmployeeModal.vue";
import ArchiveEmployeeDialog from "@components/ArchiveEmployeeDialog/ArchiveEmployeeDialog.vue";
import UnarchiveEmployeeDialog from "@components/UnarchiveEmployeeDialog/UnarchiveEmployeeDialog.vue";
import SuspendEmployeeAccess from "@components/SuspendEmployeeAccess/SuspendEmployeeAccess.vue";
import RestoreEmployeeAccess from "@components/RestoreEmployeeAccess/RestoreEmployeeAccess.vue";
import ExActionsPopUp from "@components/common/ExActionsPopUp.vue";
import ResendInviteAdminUserPopup from "@components/AdminUserActions/ResendInviteAdminUserPopup.vue";
import CancelInviteAdminUserPopup from "@components/AdminUserActions/CancelInviteAdminUserPopup.vue";
import ChecklistDrawer from "@components/Drawer/ChatterDrawer.vue";
import AssignEmployeeToPrimaryPositionDialog from "@components/AssignEmployeeToPrimaryPositionDialog/AssignEmployeeToPrimaryPositionDialog.vue";
import ExEmployeeToPreparePosition from "@components/PreparePositionPicker/ExEmployeeToPreparePosition.vue";
import RelievePrimaryAssignment from "@components/organizations/employees/RelievePrimaryAssignment.vue";
import {
  omitArchivedDefaultValue,
  selectVisibleEmployees,
} from "@components/EmployeesOrganizationTable/employeesTableFilters";
import { useEmployeesOrganizationTableViewState } from "@components/EmployeesOrganizationTable/composables/useEmployeesOrganizationTableViewState";
import { bindExiqtiveAccessFiltersChanged } from "@components/utils/bindExiqtiveAccessFiltersChanged";
import { useEmployees } from "@/composables/employees/useEmployees";
import { useEmployeeStore } from "@/store/pinia/employee";
import { requestAccessPermissionSync } from "@/utils/refreshAccessPermission";
import { isProductReadinessEnabled } from "@/utils/accountProductFlags";
import { getOrganizationEmployeeDefaultQueryTab } from "@/utils/organizationEmployeeDetailTabs";
import {
  userStateUpdateSuspend,
  userStateUpdateGiveBack,
  userCreateLinked,
  getListOfDepartments,
} from "@/api";

const app = getCurrentInstance();
const { proxy } = app || {};

const EMPLOYEES_FILTERS_KEY = "employeesOrganizationTable";

const props = defineProps({
  employees: {
    type: Array,
    default: () => [],
  },
});

const tableConfig = useTableConfig();
const viewState = useEmployeesOrganizationTableViewState();
const employees = useEmployees();
const employeeStore = useEmployeeStore();
const accountID = ref(JSON.parse(localStorage.getItem("currentAccountID")));
const readinessProductEnabled = computed(() => isProductReadinessEnabled());

/** LaunchDarkly flag `idea-MDP-432`: new give-access wizard vs legacy modal. */
const ideaMdp453GiveAccessWizard = computed(() =>
  Boolean(proxy?.$store?.getters?.["featureFlags/ideaMDP453"])
);

/** MDP-440: new FilterEmployeeModal (status + archived + department) on the Employees list; off → legacy SearchModal. */
const ideaMdp440Enabled = ref(false);
const gridFilterModalDepartments = ref([]);
const gridFilterModalDepartmentsLoading = ref(false);

/** MDP-551 / ER-10492: the four single-select radio filter groups on FilterEmployeeModal. */
const ideaMdp551Enabled = ref(false);

const gridHasFilterValue = (target) =>
  (viewState.state.filters.values || []).some(
    (f) => String(f).toLowerCase() === String(target).toLowerCase()
  );

const filterModalInitialStatuses = computed(() => ({
  active: gridHasFilterValue("active"),
  draft: gridHasFilterValue("draft"),
  terminated: gridHasFilterValue("terminated"),
}));

// ER-11658: stable value only — "Show" was the pre-refactor label string.
const filterModalInitialIncludeArchived = computed(() => gridHasFilterValue("show_archived"));

/** Initial selections for the Occupies / Profile Picture / Access / Prepares radios. */
const filterModalRadioPayload = computed(() =>
  buildEmployeeRadioFilterPayload(viewState.state.filters.values || [])
);

// Pagination and search state
const searchText = ref("");
const currentPage = ref(1);
const itemsPerPage = ref(10);
const sortBy = ref("first_name");
const sortDesc = ref(false);

// Flag to prevent duplicate API calls on mount
const isInitialLoadComplete = ref(false);
const isLoadingEmployees = ref(false);
const hasInitialFetchSettled = ref(false);

// Sync local searchText with viewState searchText
watch(
  () => viewState.state.filters.searchText,
  (newValue) => {
    if (newValue !== searchText.value) {
      searchText.value = newValue || "";
    }
  },
  { immediate: true }
);

// Use employees from composable/store if available, otherwise use props
const employeesList = computed(() => {
  const storeEmployeesRaw = employees.allEmployees.value ?? employeeStore.getEmployees ?? [];
  const storeEmployees = Array.isArray(storeEmployeesRaw) ? storeEmployeesRaw : [];
  const fallback = Array.isArray(props.employees) ? props.employees : [];
  return storeEmployees.length > 0 ? storeEmployees : fallback;
});

// Watch for changes in employees store
watch(
  () => employees.allEmployees.value,
  () => {
    // Update current page when data changes
    currentPage.value = viewState.state.table.currentPage || 1;
  },
  { deep: true }
);

// Watch for route changes to clear search when navigating away from employees tab
watch(
  () => proxy?.$route,
  (to, from) => {
    if (!proxy?.$route || !from) return;

    const currentTab = proxy.$route.params?.tab?.toLowerCase() || "";
    const previousTab = from?.params?.tab?.toLowerCase() || "";
    const currentPath = proxy.$route.path || "";
    const previousPath = from?.path || "";

    // Determine if we were on employees tab
    // Check tab param first, then fall back to path checking
    const wasOnEmployeesTab =
      previousTab === "employees" ||
      (previousPath.includes("/organization/employees") &&
        !previousPath.match(/\/organization\/employees\/\d+/));

    // Determine if we're currently on employees tab
    const isOnEmployeesTab =
      currentTab === "employees" ||
      (currentPath.includes("/organization/employees") &&
        !currentPath.match(/\/organization\/employees\/\d+/));

    const hasActiveSearch =
      Boolean(viewState.state.filters.searchText?.trim()) ||
      (viewState.state.filters.multipleFieldSearchValues || []).some(
        (field) => field.value && String(field.value).trim() !== ""
      );

    // If we were on employees tab and now we're not, clear the search
    if (wasOnEmployeesTab && !isOnEmployeesTab && hasActiveSearch) {
      viewState.clearSearch();
      searchText.value = "";
    }
  },
  { immediate: false }
);

// Use employees from store/composable (backend pagination)
// Search box "no access" shortcut: legacy client filter (active + access) when not fully server-aligned.
// Otherwise selectVisibleEmployees decides: MDP-440 trusts the API result, legacy path re-filters chips.
const paginatedEmployees = computed(() => {
  const list = Array.isArray(employeesList.value) ? employeesList.value : [];
  const filterValues = viewState.state.filters.values;

  const isSearchTextActive =
    searchText.value?.toLowerCase()?.trim() === "no access" ||
    searchText.value?.toLowerCase()?.trim() === "no_access";

  const accessMatchesNoAccess = (emp) => {
    if (emp.access == null) return true;
    if (emp.access?.has_access === false) return true;
    const accessStatus = viewState.getEmployeeAccess(emp.access);
    return accessStatus === "no-access" || accessStatus === "invited";
  };

  if (isSearchTextActive) {
    return list.filter((emp) => emp.status === "active" && accessMatchesNoAccess(emp));
  }

  return selectVisibleEmployees(list, filterValues, ideaMdp440Enabled.value);
});

// ER-12353: the untouched list carries the registered defaults (Draft, Active), which
// count as "no filters applied".
function hasNoFiltersApplied(values) {
  return areDefaultFiltersApplied(EMPLOYEES_FILTERS_KEY, values);
}

const hasNoItems = computed(() => {
  const itemsList = paginatedEmployees.value;
  return !Array.isArray(itemsList) || itemsList.length === 0;
});

// The first fetch has finished and no list request is in flight, so an empty list is real
// (not "not loaded yet") and the empty-account state may replace the filter bar.
const isListSettled = computed(
  () => hasInitialFetchSettled.value && !employeeStore.isLoadingList
);

// While an empty list is (re)loading show the skeleton, never the empty-account message.
const isTableLoading = computed(
  () => isLoadingEmployees.value || (!isListSettled.value && hasNoItems.value)
);

// ER-10611: a multi-field search (name / primary position / preparing for) is active.
const hasMultipleFieldSearch = computed(() =>
  (viewState.state.filters.multipleFieldSearchValues || []).some(
    (f) => f.value && String(f.value).trim() !== ""
  )
);

const hideSearchAndFilters = computed(() => {
  const hasNoTextSearch = !String(
    viewState.state.filters.searchText || searchText.value || ""
  ).trim();
  // ER-10611: keep the search row visible while a multi-field search is active so the
  // user can still see/clear the selected field chips even when the result set is empty.
  const hasNoSearch = hasNoTextSearch && !hasMultipleFieldSearch.value;
  const hasNoFilters = hasNoFiltersApplied(viewState.state.filters.values);
  // MDP-440: an empty result driven by a department filter is not an empty account, so keep
  // the filter bar (and its chips) visible to let the user clear or change that filter.
  const hasDepartmentFilter =
    ideaMdp440Enabled.value && viewState.state.filters.department?.id != null;
  return (
    isListSettled.value &&
    hasNoItems.value &&
    hasNoSearch &&
    hasNoFilters &&
    !hasDepartmentFilter
  );
});

// Add filters to table config (ensure searchText is always a string so UniversalTable empty state shows correctly)
const tableConfigWithFilters = computed(() => {
  const visibleFilterValues = omitArchivedDefaultValue(viewState.state.filters.values);
  return {
    ...tableConfig.tableParams.value,
    filters: {
      key: EMPLOYEES_FILTERS_KEY,
      values: visibleFilterValues,
      chipLabels: viewState.getFilterDisplayLabels(visibleFilterValues),
      searchText: searchText.value ?? "",
      // ER-12353: lets the empty state offer Clear Search/Clear All for a multi-field search.
      hasMultipleFieldSearch: hasMultipleFieldSearch.value,
      searchGhostText: viewState.state.filters.searchGhostText,
    },
    hideSearchAndFilters: hideSearchAndFilters.value,
  };
});

// Get total count from store pagination
// The API returns the correct total count based on applied filters, so we should use that
// instead of counting the current page's results
const filteredCount = computed(() => {
  const fromPagination = employeeStore.pagination?.totalCount;
  if (fromPagination != null) {
    return fromPagination;
  }
  const fromStoreTotal = employeeStore.totalEmployeesCount;
  if (fromStoreTotal != null) {
    return fromStoreTotal;
  }
  return employeesList.value.length || 0;
});

const handleSearch = (search) => {
  searchText.value = search;
  currentPage.value = 1;
  // Use viewState's handleSearch which calls the API
  viewState.handleSearch(search);
};

const handleMultipleFieldSearch = async (event) => {
  currentPage.value = 1;
  // Use viewState's handleMultipleFieldSearch which calls the API
  await viewState.handleMultipleFieldSearch(event);
};

const handleSort = (sortData) => {
  // Use viewState's handleEmployeeSort which calls the API
  // sortData comes from UniversalTable as { field, descending, ascending }
  viewState.handleEmployeeSort({
    field: sortData.field,
    descending: sortData.descending,
  });
};

const handleRequest = async ({ page, itemsPerPage: perPage }) => {
  if (page === 1 && !isInitialLoadComplete.value) {
    return;
  }

  // Prevent concurrent API calls
  if (isLoadingEmployees.value) {
    return;
  }

  isLoadingEmployees.value = true;
  try {
    currentPage.value = page;
    itemsPerPage.value = perPage || 10;
    // Use viewState's handleRequest which calls the API
    await viewState.handleRequest({ page, itemsPerPage: perPage || 10 });
  } finally {
    isLoadingEmployees.value = false;
  }
};

const applyFilters = async (filters) => {
  // Use viewState's applyFilters which handles API calls
  await viewState.applyFilters(filters);
  currentPage.value = 1;
};

const removeFilter = async (filter) => {
  const filterValue = viewState.getFilterValueFromLabel(
    typeof filter === "object" ? filter.value : filter
  );
  const currentValues = viewState.state.filters.values;
  const values = Array.isArray(currentValues) ? currentValues : [];
  viewState.state.filters.values = values.filter((entry) => {
    const entryValue = typeof entry === "object" ? entry.value : entry;
    return entryValue !== filterValue;
  });

  // Use viewState's removeFilters which handles API calls
  await viewState.removeFilters(filter);
  currentPage.value = 1;
};

const removeAllFilters = async () => {
  // Use viewState's removeAllFiltersAndReload which handles API calls
  await viewState.removeAllFiltersAndReload();
  currentPage.value = 1;
};

const handleGridDepartmentSearch = async (query) => {
  gridFilterModalDepartmentsLoading.value = true;
  try {
    if (!accountID.value) {
      gridFilterModalDepartments.value = [];
      return;
    }
    const { data } = await getListOfDepartments(
      accountID.value,
      query || "",
      { archived: false },
      { limit: 500, offset: 0 }
    );
    const results = Array.isArray(data) ? data : data?.results || [];
    gridFilterModalDepartments.value = mapApiDepartmentsToPickerList(results);
  } catch (error) {
    console.error("Error loading departments for employee filter:", error);
    gridFilterModalDepartments.value = [];
  } finally {
    gridFilterModalDepartmentsLoading.value = false;
  }
};

const handleGridFilterModalApply = async (payload) => {
  const newFilters = Array.isArray(payload?.status) ? [...payload.status] : [];
  if (payload?.includeArchived) {
    newFilters.push("show_archived");
  }
  // ER-10492 / MDP-551: single-select radio filter groups — non-default values
  // become applied filter values (chips + API query params via sync).
  newFilters.push(...payloadToEmployeeRadioFilterValues(payload));
  // Set department before applyFilters so syncEmployeeApiFiltersFromState picks it up.
  viewState.setDepartmentFilter(payload?.department || null, payload?.scope || "subtree");
  viewState.state.filters.showFilterDialog = false;
  await viewState.applyFilters(newFilters);
  currentPage.value = 1;
};

const handleGridToggleDepartmentScope = async () => {
  const newScope = viewState.state.filters.departmentScope === "direct" ? "subtree" : "direct";
  viewState.setDepartmentFilter(viewState.state.filters.department, newScope);
  employees.setPaginationDataOffset(0);
  await viewState.reloadEmployeesPreservingFilters();
  currentPage.value = 1;
};

const handleGridClearDepartmentFilter = async () => {
  viewState.setDepartmentFilter(null);
  employees.setPaginationDataOffset(0);
  await viewState.reloadEmployeesPreservingFilters();
  currentPage.value = 1;
};

// ER-12353: remembers whether the dialog was opened from the empty-account state, where
// saving must keep the user on the list instead of drilling into the new employee.
const addedFromEmptyAccount = ref(false);

const handleAddEmployee = () => {
  addedFromEmptyAccount.value = hideSearchAndFilters.value;
  employees.setCurrentEmployee(null);
  viewState.state.addEmployeeModal.show = true;
};

const handleSaveEmployee = async (employeeData) => {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  const isNewEmployee = !employeeData?.id;
  const stayOnList = isNewEmployee && addedFromEmptyAccount.value;

  const result = await viewState.handleEmployeeOperation(
    employees.operations.EDIT_EMPLOYEE,
    accountId,
    employeeData,
    { stayOnList }
  );

  // If it's a new employee and we got the created employee data, emit drilldown
  if (isNewEmployee && !stayOnList && result?.createdEmployee?.id) {
    // Wait for route to update after navigation
    await proxy.$nextTick();

    // Emit drilldown event to immediately show employee detail
    // This ensures the UI updates immediately without waiting for route watcher
    emit("drilldown", {
      employeeId: String(result.createdEmployee.id),
      tab: getOrganizationEmployeeDefaultQueryTab(),
    });
  }
};

const handleActivateEmployee = async () => {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  await viewState.handleEmployeeOperation(employees.operations.ACTIVATE_EMPLOYEE, accountId);
};

const handleDeleteEmployee = async () => {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  await viewState.handleEmployeeOperation(employees.operations.DELETE_EMPLOYEE, accountId);
};

const handleTerminateEmployee = async (payload) => {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  await viewState.handleEmployeeOperation(
    employees.operations.TERMINATE_EMPLOYEE,
    accountId,
    payload
  );
};

const handleReactivateEmployee = async () => {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  await viewState.handleEmployeeOperation(employees.operations.REACTIVATE_EMPLOYEE, accountId);
};

const handleChangePicture = async (croppedImage) => {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  await viewState.handleEmployeeOperation(
    employees.operations.CHANGE_EMPLOYEE_PICTURE,
    accountId,
    croppedImage
  );
};

const handleRemovePicture = async () => {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  await viewState.handleEmployeeOperation(
    employees.operations.CHANGE_EMPLOYEE_PICTURE,
    accountId,
    null
  );
};

const handleCheckEmail = async (email) => {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  try {
    await viewState.checkUserEmail({ accountId, email });
  } catch (error) {
    console.error("Error checking email:", error);
    if (proxy?.$toast) {
      proxy.$toast.error("Network error. Please check your connection and try again.");
    } else if (proxy?.$notifications) {
      proxy.$notifications.error("Network error. Please check your connection and try again.");
    }
  }
};

/**
 * GET check-email: validates whether the address is usable (handled in GiveAccessToEmployeeWizard).
 */
const fetchGiveAccessEmailCheck = async (email) => {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  const trimmed = (email || "").trim();
  try {
    const response = await employees.checkEmailUser({
      accountId,
      email: trimmed,
    });
    return mapCheckEmailApiToGiveAccessHandlerResult(response);
  } catch (error) {
    console.error("Give access email check failed:", error);
    if (proxy?.$toast) {
      proxy.$toast.error("Network error. Please check your connection and try again.");
    } else if (proxy?.$notifications) {
      proxy.$notifications.error("Network error. Please check your connection and try again.");
    }
    return { available: true };
  }
};

const giveAccessEmailCheck = fetchGiveAccessEmailCheck;

/** user-create-linked body: name + email only. */
const buildUserCreateLinkedRequestBody = (payload) => ({
  first_name: payload.first_name,
  last_name: payload.last_name,
  email: (payload.email && String(payload.email).trim()) || payload.email,
});

/**
 * POST user-create-linked (v2) — link invite flow for MDP-432 wizard.
 */
const performUserCreateLinked = (accountId, employeeId, payload) => {
  const body = buildUserCreateLinkedRequestBody(payload);
  return userCreateLinked(accountId, employeeId, body);
};

const handleGiveAccess = async (payload) => {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  if (ideaMdp453GiveAccessWizard.value) {
    const employeeId = payload.employee_id;
    if (!employeeId) {
      proxy?.$notifications?.error("Missing employee. Cannot complete give access.");
      return;
    }
    proxy?.$loader?.setAppLoading(true);
    try {
      await performUserCreateLinked(accountId, employeeId, payload);
      await viewState.reloadEmployeesPreservingFilters();
      requestAccessPermissionSync(proxy?.$root);
      proxy?.$loader?.setAppLoading(false);
      viewState.state.giveAccessModal.show = false;
      viewState.state.giveBackAccessModal.show = false;
    } catch (error) {
      proxy?.$loader?.setAppLoading(false);
      let errorMessage = "An error occurred while giving access to the employee.";
      if (error.response?.data) {
        const errorData = error.response.data;
        if (errorData.account_permission?.non_field_errors) {
          errorMessage =
            "You can't give access to the owner's email. Only the account owner can do that.";
        }
      }
      proxy?.$notifications?.error(errorMessage);
    }
    return;
  }
  await viewState.giveAccessToEmployee({
    accountId,
    payload,
  });
};

const handleGiveBackAccess = async (payload) => {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  await viewState.giveAccessToEmployee({
    accountId,
    payload,
  });
};

const handleArchiveEmployee = async () => {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  await viewState.handleEmployeeOperation(employees.operations.ARCHIVE_EMPLOYEE, accountId);
};

const handleSuspendAccess = async (payload) => {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  const currentEmployee = employees.getCurrentEmployee();

  // Get access_id from various possible locations
  const access_id =
    currentEmployee?.access_id ||
    currentEmployee?.access?.access_id ||
    currentEmployee?.access?.id ||
    currentEmployee?.user?.id;

  if (!access_id) {
    proxy?.$toast?.error("Could not find access information for this employee.");
    viewState.state.suspendEmployeeAccessModal.show = false;
    return;
  }

  try {
    const obj = {
      email: currentEmployee?.email,
      state: "suspended",
      notify: payload?.notify || false,
    };

    await userStateUpdateSuspend(accountId, access_id, obj);
    viewState.state.suspendEmployeeAccessModal.show = false;
    await viewState.reloadEmployeesPreservingFilters();
  } catch (error) {
    console.error("Error suspending access:", error);
    proxy?.$toast?.error("Failed to suspend access. Please try again.");
    viewState.state.suspendEmployeeAccessModal.show = false;
  }
};

const handleGiveBackAccessUser = async (payload) => {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  const currentEmployee = employees.getCurrentEmployee();

  // Get access_id from various possible locations (same as handleSuspendAccess)
  const access_id =
    currentEmployee?.access_id ||
    currentEmployee?.access?.access_id ||
    currentEmployee?.access?.id ||
    currentEmployee?.user?.id;

  if (!access_id) {
    proxy?.$toast?.error("Could not find access information for this employee.");
    viewState.state.giveBackEmployeeAccessModal.show = false;
    return;
  }

  try {
    await userStateUpdateGiveBack(accountId, access_id, {
      email: currentEmployee?.email,
      state: "operational",
      notify: payload?.notify || false,
    });
    viewState.state.giveBackEmployeeAccessModal.show = false;
    await viewState.reloadEmployeesPreservingFilters();
  } catch (error) {
    console.error("Error restoring access:", error);
    proxy?.$toast?.error("Failed to restore access. Please try again.");
    viewState.state.giveBackEmployeeAccessModal.show = false;
  }
};

const reloadEmployeesFromAccessScopedFilters = async () => {
  viewState.clearSearch();
  searchText.value = "";

  employees.clearEmployeeDynamicFilters();
  employees.removeEmployeeFilter("first_name");
  employees.removeEmployeeFilter("last_name");
  employees.removeEmployeeFilter("name");
  employees.removeEmployeeFilter("primary_position_name");
  employees.removeEmployeeFilter("preparing_position_name");
  employees.removeEmployeeFilter("status");
  employees.removeEmployeeFilter("archived");
  employees.removeEmployeeFilter("has_access");
  employees.removeEmployeeFilter("has_assignment");
  employees.setPaginationDataOffset(0);
  viewState.loadFiltersOnReload();
  if (!viewState.state.filters.values || viewState.state.filters.values.length === 0) {
    viewState.state.filters.values = ["draft", "active"];
    const filterModule = await import("@/store/pinia/filters");
    filterModule.default.setFilters({
      key: viewState.state.filters.key,
      filters: ["draft", "active"],
    });
  }

  await proxy?.$nextTick?.();

  const rawFilterValues = viewState.state.filters.values;
  const filterValuesArray = Array.isArray(rawFilterValues) ? rawFilterValues : [];
  const filtersToApply = filterValuesArray
    .map((f) => {
      if (typeof f === "string") return f;
      if (typeof f === "object" && f.value) return f.value;
      return f;
    })
    .filter(Boolean);

  if (isLoadingEmployees.value) {
    return;
  }

  isLoadingEmployees.value = true;

  try {
    if (filtersToApply && filtersToApply.length > 0) {
      await viewState.applyFilters(filtersToApply);
    } else {
      await employees.loadAllEmployees();
    }
  } finally {
    isLoadingEmployees.value = false;
  }
};

// Initialize on mount
const loadInitialEmployees = async () => {
  try {
    ideaMdp440Enabled.value = (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_440, false)) === true;
    ideaMdp551Enabled.value = (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_551, false)) === true;
  } catch {
    ideaMdp440Enabled.value = false;
    ideaMdp551Enabled.value = false;
  }

  // ER-12547: the view state's initializeFilters is not run for this list, so the
  // avatar-click flag has to be resolved here or it stays at its `false` default.
  try {
    viewState.state.featureFlags.avatarOpensChangePicture =
      (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_450, false)) === true;
  } catch {
    viewState.state.featureFlags.avatarOpensChangePicture = false;
  }

  if (isInitialLoadComplete.value) return;

  // Set current page from viewState
  currentPage.value = viewState.state.table.currentPage || 1;
  itemsPerPage.value = viewState.state.table.itemsPerPage || 10;
  await viewState.initializeFilters();

  // Check if coming from dashboard with searchEmployee query parameter
  const route = proxy?.$route;
  const router = proxy?.$router;
  const fromDashboard = route?.query?.fromDashboard === "true";
  const searchEmployeeName = route?.query?.searchEmployee || "";

  if (fromDashboard && searchEmployeeName && searchEmployeeName.trim() !== "") {
    // Wait a bit to ensure filters are cleared by OrganizationPage
    await proxy.$nextTick();
    await new Promise((resolve) => setTimeout(resolve, 300));

    // Decode the employee name
    const decodedName = decodeURIComponent(searchEmployeeName.trim());

    // Set search text in both state and local ref
    viewState.state.filters.searchText = decodedName;
    searchText.value = decodedName;

    // Wait for state to propagate
    await proxy.$nextTick();
    if (isLoadingEmployees.value) {
      return;
    }

    isLoadingEmployees.value = true;
    isInitialLoadComplete.value = true;

    try {
      await viewState.performSearch(decodedName);
    } finally {
      isLoadingEmployees.value = false;
    }

    // Wait for search to complete
    await proxy.$nextTick();

    // Remove query parameters from URL after search is done
    if (router && route) {
      router.replace({
        path: route.path,
        query: {},
      });
    }
  } else {
    await reloadEmployeesFromAccessScopedFilters();
    isInitialLoadComplete.value = true;
  }
};

onMounted(async () => {
  try {
    await loadInitialEmployees();
  } finally {
    hasInitialFetchSettled.value = true;
  }
});

let unbindAccessFiltersChanged = () => {};
onMounted(() => {
  unbindAccessFiltersChanged = bindExiqtiveAccessFiltersChanged(() => {
    reloadEmployeesFromAccessScopedFilters();
  });
});

// Clear search when component is unmounted (e.g., navigating to other tabs)
// This is a safety net in case the route watcher doesn't catch all navigation scenarios
onBeforeUnmount(() => {
  if (!proxy?.$route) return;

  const currentPath = proxy.$route.path || "";
  const currentTab = proxy.$route.params?.tab?.toLowerCase() || "";

  // Check if we're navigating to a different tab (not employees tab)
  // Employee detail pages (/organization/employees/123) are still within employees context
  const isEmployeeDetailPage = currentPath.match(/\/organization\/employees\/\d+/);
  const isStillOnEmployeesTab = currentTab === "employees" || isEmployeeDetailPage;

  const hasActiveSearch =
    Boolean(viewState.state.filters.searchText?.trim()) ||
    (viewState.state.filters.multipleFieldSearchValues || []).some(
      (field) => field.value && String(field.value).trim() !== ""
    );

  // Clear search if navigating away from employees tab to a different tab
  if (!isStillOnEmployeesTab && hasActiveSearch) {
    viewState.clearSearch();
    searchText.value = "";
  }
});

// Always clear search when component unmounts to prevent persistence across page/component changes
onUnmounted(() => {
  unbindAccessFiltersChanged();
  viewState.clearSearch();
  searchText.value = "";
});
</script>

<style scoped>
.icon-container {
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
}
.icon-svg {
  display: block;
}
::v-deep .custom-assign .allocation_icon > .content {
  top: 45%;
}
.employees-organization-table .ex-universal-table-container {
  box-shadow:
    0px 1px 2px 0px #1018280f,
    0px 1px 3px 0px #1018281a !important;
  border-radius: 8px;
}
</style>
